import { Fragment, useEffect, useRef, useState, KeyboardEvent, useMemo } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useFloating, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/react';
import { useInView } from 'react-intersection-observer';
import { createPortal } from 'react-dom';
import { Container, Display, List, ListItem, SelectedOption, Spacer } from './index.styles';
import { Button } from '../Button';

const REMOVAL_BUTTON_SELECTOR = '[data-prevent-dropdown="true"]';

export interface Option {
  /**
   * Option action. The string returned upon option selection.
   */
  action: string;
  /**
   * Adds a line before this option. Optional.
   */
  isHighlighted?: boolean;
  /**
   * Prevents the option from being removed and ensures it is always selected. Optional.
   */
  isLocked?: boolean;
  /**
   * Option label text. Ex.: Translation.
   */
  label: string;
}

export interface TaggableInputProps {
  className?: string;
  customClass?: string;
  /**
   * Empty state label for the list. Required.
   */
  emptyStateLabel: string;
  /**
   * Makes list min-width the same as the display section. Optional.
   */
  fitList?: boolean;
  /**
   * Component ID. Required.
   */
  id: string;
  /**
   * Emits an event to the invoker component when an option is selected, sending the selected option. Required.
   */
  onSelect: (selectedOptions: Option[]) => void;
  /**
   * List of selectable options. Required.
   */
  options: Option[];
  /**
   * Placeholder text for the input. Required.
   */
  placeholder: string;
  /**
   * List opening position. Optional.
   */
  placement?: Placement;
  /**
   * List of options already selected. It should be an array when using input type stackable. Optional.
   */
  selectedOptions: Option[];
  /**
   * Optional maximum number of options that can be selected. When reached, further selections are disabled.
   */
  limit?: number;
  testId?: string;
}

export const TaggableInput = ({
  className,
  customClass,
  emptyStateLabel,
  fitList,
  id,
  onSelect,
  options,
  placeholder,
  placement = 'bottom-end',
  selectedOptions = [],
  limit,
  testId = 'taggable-input',
}: TaggableInputProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [filter, setFilter] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLLIElement | null>(null);
  const firstOpenRef = useRef(true);

  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    rootMargin: '8px',
  });

  const { x, y, strategy, refs, elements } = useFloating({
    open: isCollapsed,
    onOpenChange: setIsCollapsed,
    placement,
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const availableOptions = useMemo(() => {
    const base = options.filter((option) => !option.isLocked && !selectedOptions.some((sel) => sel.action === option.action));
    const f = filter.trim().toLowerCase();

    if (!f) return base;

    return base.filter((opt) => opt.label.toLowerCase().includes(f));
  }, [options, selectedOptions, filter]);

  useEffect(() => {
    const lockedOptions = options.filter((option) => option.isLocked);

    if (!lockedOptions.length) return;

    const missingLocked = lockedOptions.filter((lockedOption) => !selectedOptions.some((selected) => selected.action === lockedOption.action));

    if (missingLocked.length) {
      onSelect([...selectedOptions, ...missingLocked]);
    }
  }, [onSelect, options, selectedOptions]);

  const isLimitReached = useMemo(() => typeof limit === 'number' && limit >= 0 && selectedOptions.length >= limit, [limit, selectedOptions.length]);

  const handleSelection = (option: string, operation: 'add' | 'remove') => {
    if (!onSelect) return;

    if (availableOptions.length === 0 && inputRef.current) {
      inputRef.current.value = '';
      setFilter('');
    }

    if (operation === 'add') {
      if (isLimitReached) return;

      const item = options.find((opt) => opt.action === option) as Option;
      const next = [...selectedOptions, item];

      onSelect(next);

      const totalSelectable = options.length;

      if (next.length === totalSelectable || (typeof limit === 'number' && next.length >= limit)) {
        setIsCollapsed(false);
        if (inputRef.current) {
          inputRef.current.value = '';
          setFilter('');
        }
      }
    } else {
      const optionConfig = options.find((opt) => opt.action === option);
      const selectedItem = selectedOptions.find((opt) => opt.action === option);

      if (optionConfig?.isLocked || selectedItem?.isLocked) return;

      const newOptions = selectedOptions.filter((opt) => opt.action !== option);

      onSelect(newOptions);
    }

    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (!target.id.includes(id)) {
        setIsCollapsed(false);

        if (inputRef.current) {
          inputRef.current.value = '';
          setFilter('');
        }
      }
    };

    document.addEventListener('mouseup', handleClickOutside);

    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, [id]);

  useEffect(() => {
    if (isCollapsed && !inView) {
      queueMicrotask(() => {
        setIsCollapsed(false);
      });
    }
  }, [inView, isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) return;
    if (firstOpenRef.current) {
      firstOpenRef.current = false;
    }
  }, [isCollapsed, availableOptions.length]);

  useEffect(() => {
    if (!isCollapsed) return;

    const inputEl = inputRef.current;

    if (inputEl && document.activeElement === inputEl) return;
    listRef.current?.focus();
  }, [isCollapsed]);

  const optionList = availableOptions.map((option, index) => (
    <Fragment key={option.action}>
      {option.isHighlighted && (
        <Spacer
          role="presentation"
          tabIndex={-1}
        />
      )}
      <ListItem
        data-testid={`${testId}-li-${index}`}
        id={`${id}-li-${option.action}-${index}`}
        onClick={() => !isLimitReached && handleSelection(option.action, 'add')}
        ref={(el: HTMLLIElement | null) => {
          if (index === 0) listRef.current = el;
        }}
        role="menuitem"
        tabIndex={0}
        $disabled={isLimitReached}
        aria-disabled={isLimitReached}
        title={option.label}
      >
        {option.label}
      </ListItem>
    </Fragment>
  ));

  const portalTarget = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;
  const listContent = (
    <List
      className="ds-taggable-input-list ds-taggable-input-list"
      data-testid={`${testId}-list`}
      id={`${id}-list`}
      $minWidth={fitList && elements.reference ? elements.reference.getBoundingClientRect().width : 0}
      ref={(node: HTMLDivElement | null) => refs.setFloating(node)}
      style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
      tabIndex={-1}
    >
      <div
        id={`${id}-content`}
        tabIndex={-1}
      >
        <ul
          aria-labelledby={`taggable-${id}`}
          id={`${id}-ul`}
          ref={inViewRef}
          role="menu"
          tabIndex={-1}
        >
          {availableOptions.length > 0 ? (
            optionList
          ) : (
            <ListItem
              $disabled
              tabIndex={-1}
            >
              {emptyStateLabel}
            </ListItem>
          )}
        </ul>
      </div>
    </List>
  );

  return (
    <Container
      className={`ds-taggable-input ds-taggable-input ${customClass} ${className}`}
      data-testid={testId}
      id={id}
    >
      {/** DISPLAY AREA */}
      <Display
        className="ds-taggable-input-display ds-taggable-input-display"
        data-testid={`${testId}-display`}
        id={`${id}-display`}
        onClick={(event?: ReactMouseEvent<HTMLDivElement>) => {
          const targetElement = (event?.target as Element | null) ?? null;

          if (targetElement?.closest(REMOVAL_BUTTON_SELECTOR)) return;

          setIsCollapsed(true);
          if (inputRef.current) inputRef.current.focus();
        }}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsCollapsed((prev) => !prev)}
        ref={(node: HTMLDivElement | null) => refs.setReference(node)}
        tabIndex={0}
        $hasSelection={selectedOptions.length > 0}
      >
        {selectedOptions.map((option, index) => (
          <SelectedOption
            data-testid={`${testId}-selected-option-${index}`}
            key={`option-${option.action}`}
            id={`so-${id}`}
            tabIndex={0}
            data-prevent-dropdown="true"
          >
            <span id={`so-${id}-label-${option.action}`}>{option.label}</span>
            {!(option.isLocked ?? options.find((opt) => opt.action === option.action)?.isLocked) && (
              <Button
                data-testid={`${testId}-selected-option-${index}-remover`}
                icon="close"
                id={`so-${id}-btn-${option.action}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelection(option.action, 'remove');
                }}
                size={12}
                tabIndex={0}
              />
            )}
          </SelectedOption>
        ))}
        <input
          autoComplete="off"
          id={`${id}-input`}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={({ key, currentTarget }: KeyboardEvent<HTMLInputElement>) => {
            if (key === 'Enter') setFilter(currentTarget.value);
          }}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          aria-label={isLimitReached ? `Maximum of ${limit} selections reached` : undefined}
          disabled={isLimitReached}
        />
      </Display>

      {/** DROPDOWN AREA */}
      {isCollapsed && (portalTarget ? createPortal(listContent, portalTarget) : listContent)}
    </Container>
  );
};
